import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  CallDeleteOpt,
  CallDeleteOptGroup,
  CallGetOptionGroup,
  CallPostOptionGroup,
} from '@library/request/callProduct';
import { useMenuPermission } from '@src/recoil/permission';
import { MenuEmptyBox, BottomPopup, NoAuthBox } from '@src/pages/product/componnents/common';
import {
  BottomPopupType,
  MenuManageTabProps,
  OptionGroupChild,
  OptionGroups,
  OptParams,
  PRODUCT_KEYS,
} from '@type/productType';
import CommonEditIconButton from '@components/molecules/button/EditIconButton';
import { useRecoilState } from 'recoil';
import { optGroupId, menuScroll } from '@src/recoil/menu';

const OptionManage = (props: MenuManageTabProps) => {
  const { storeId, setLoading } = props;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { menuPermission, isNoAuthOpt, isNoAuth1, isNoAuth8, isNoAuth9 } = useMenuPermission(storeId);

  const [isBottomPopup, setIsBottomPopup] = useState<boolean>(false); // 하단 팝업 노출 유무
  const [type, setType] = useState<BottomPopupType>('optionGroup'); // 옵션 그룹 or 옵션
  const [selectedOptGroup, setSelectedOtpGroup] = useState<OptionGroups>(null); // 선택한 옵션 그룹

  const clickedListRef = useRef<{ id: string; title: string; optionGroupID?: string }>(null); // 클릭한 옵션그룹 or 옵션
  const optGroupIdRef = useRef<string>(null); // 클릭한 옵션그룹 ID

  const [currentOptGroupId, setOptGroupId] = useRecoilState(optGroupId);
  const [menuScrollValue, setMenuScrollValue] = useRecoilState(menuScroll);

  // 옵션그룹 리스트 조회 API
  const { data: optionGroup } = useQuery([PRODUCT_KEYS.OPTIONGROUP, storeId], {
    enabled: !!storeId && menuPermission.isFetch && (!menuPermission.data.length || !isNoAuthOpt),
    queryFn: async () => {
      try {
        setLoading(true);
        const response = await CallGetOptionGroup(storeId);
        if (response?.result.code !== 1) return;
        const { data } = response;
        return data;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  // 옵션그룹 복사 API
  const { mutate: copyOptGroupMutation } = useMutation((param: any) => CallPostOptionGroup(storeId, param), {
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: (response) => {
      if (response?.result.code !== 1) {
        alert(response?.result?.message);
      } else {
        // 옵션그룹 재조회
        queryClient.invalidateQueries([PRODUCT_KEYS.OPTIONGROUP, storeId]);
      }
    },
    onError: (error) => {
      console.log('error:', error);
    },
    onSettled: () => {
      setLoading(false);
      setIsBottomPopup(false);
    },
  });

  // 옵션그룹 삭제 API
  const { mutate: deleteOptGroupMutation } = useMutation(
    (optGroupID: string) => CallDeleteOptGroup(storeId, optGroupID),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: (response) => {
        if (response?.result.code !== 1) {
          alert(response?.result?.message);
        } else {
          // 옵션그룹 재조회
          queryClient.invalidateQueries([PRODUCT_KEYS.OPTIONGROUP, storeId]);
        }
      },
      onError: (error) => {
        console.log('error:', error);
      },
      onSettled: () => {
        setLoading(false);
        setIsBottomPopup(false);
      },
    },
  );

  // 옵션 삭제 API
  const { mutate: deleteOptMutation } = useMutation(
    ({ optId, param }: { optId: string; param: any }) => CallDeleteOpt(storeId, optId, param),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: (response) => {
        if (response?.result.code !== 1) {
          alert(response?.result?.message);
        } else {
          // 옵션그룹 재조회
          queryClient.invalidateQueries([PRODUCT_KEYS.OPTIONGROUP, storeId]);
        }
      },
      onError: (error) => {
        console.log('error:', error);
      },
      onSettled: () => {
        setLoading(false);
        setIsBottomPopup(false);
      },
    },
  );

  // 옵션그룹 삭제
  const onClickOptGroupDelete = () => {
    if (!optionGroup) return;
    const selectedOpt = optionGroup.find((category) => category.ID.toString() === clickedListRef.current.id);

    if (!window.confirm('해당 옵션 그룹을 삭제하시겠습니까?\n삭제 후 다시 취소하실 수 없습니다.')) {
      return;
    }

    if (selectedOpt.products.length > 0) {
      window.alert('현재 옵션 그룹과 연결된 메뉴가 있습니다.\n연결된 메뉴를 모두 해제 후 삭제할 수 있습니다.');
      return;
    }
    deleteOptGroupMutation(selectedOpt.ID.toString());
  };

  // 옵션 삭제
  const onClickOptDelete = (selectedOpt: OptionGroups) => {
    if (!selectedOpt) return;

    if (selectedOpt.children.length == 1) {
      window.alert('옵션 그룹에 옵션이 최소 1개 이상 존재해야 합니다.');
      return;
    }

    if (!window.confirm('해당 옵션을 삭제하시겠습니까?\n삭제 후 다시 취소하실 수 없습니다.')) {
      return;
    }

    const param: OptParams = { options: [] };
    const options = {
      optionGroupID: selectedOpt.ID,
      isPriceOption: selectedOpt.isPriceOption,
      title: selectedOpt.title,
      selectType: selectedOpt.selectType,
      isDisplay: selectedOpt.isDisplay,
      children: selectedOpt.children.filter((option) => option.optionID !== Number(clickedListRef.current.id)), // 안전하게 필터링
    };
    param.options.push(options);

    deleteOptMutation({ optId: selectedOpt.ID.toString(), param });
  };

  // 옵션그룹 복사
  const onClickCopyOptGroup = () => {
    if (!optionGroup) return;

    const optGroup = optionGroup.find((category) => category.ID.toString() === clickedListRef.current.id);
    const param: OptParams = { options: [] };

    const children =
      optGroup.children?.map((option) => ({
        name: option.name,
        price: option.price,
        status: option.status,
        isDisplay: option.isDisplay,
        alcohol: option.alcohol,
        maxSelect: option.maxSelect,
        externalMenuCode: option.externalMenuCode,
      })) || [];

    param.options.push({
      title: optGroup.title,
      isDisplay: optGroup.isDisplay,
      selectType: optGroup.selectType,
      isRequired: optGroup.isRequired,
      maxSelect: optGroup.maxSelect,
      minSelect: optGroup.minSelect,
      children,
    });

    copyOptGroupMutation(param);
  };

  useEffect(() => setType('optionGroup'), [storeId]);

  useEffect(() => {
    if (!optionGroup || !optGroupIdRef.current) return;

    const sameOtpGroup = optionGroup.find((category) => category.ID.toString() === optGroupIdRef.current);
    setSelectedOtpGroup(sameOtpGroup ?? null);
  }, [optGroupIdRef.current, optionGroup]);

  useEffect(() => {
    if (currentOptGroupId) {
      optGroupIdRef.current = currentOptGroupId;
      setType('option');
    }
  }, []);

  useEffect(() => {
    if (type === 'optionGroup' || menuScrollValue.depth1 > 0) {
      window.scrollTo(0, menuScrollValue.depth1);
    }
    if (type === 'option' || menuScrollValue.depth2 > 0) {
      window.scrollTo(0, menuScrollValue.depth2);
    }
  }, [type]);

  const ListElement = ({ list }: { list: (OptionGroups | OptionGroupChild)[] }) => {
    const isOptGroup = (item: OptionGroups | OptionGroupChild): item is OptionGroups => {
      return (item as OptionGroups).ID !== undefined;
    };

    return (
      <ul>
        {list.map((item) => {
          const id = isOptGroup(item) ? item.ID : item.optionID;
          const title = isOptGroup(item) ? item.title : item.name;

          return (
            <li key={id} className={`${item.isDisplay === 0 ? 'off' : ''} ${item.status === 4 ? 'soldout' : ''}`}>
              <div className="item-product" data-product-id={id}>
                <button
                  type="button"
                  className="txt-product option"
                  onClick={() => {
                    if (isOptGroup(item)) {
                      setMenuScrollValue({
                        depth1: window.scrollY,
                        depth2: 0,
                      });
                      setType('option');
                      clickedListRef.current = { id: id.toString(), title };
                      optGroupIdRef.current = id.toString();
                      setOptGroupId(id.toString());
                    }
                  }}
                >
                  {title}
                  {item.isRequired === 1 && item.isDisplay === 1 && <span className="red">*필수선택</span>}
                  <span className="txt-state" />
                </button>
                <button
                  type="button"
                  className="li-btn-more"
                  onClick={() => {
                    if (isOptGroup(item)) {
                      optGroupIdRef.current = id.toString();
                      clickedListRef.current = { id: id.toString(), title };
                    } else {
                      clickedListRef.current = { id: id.toString(), title, optionGroupID: optGroupIdRef.current };
                    }

                    setIsBottomPopup(true);
                  }}
                >
                  <span className="ico-more" />
                  더보기
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div>
      {isNoAuthOpt ? (
        <NoAuthBox type="option" />
      ) : (
        <>
          {type === 'optionGroup' ? (
            // 옵션 그룹
            <div className="box-content">
              <h4 className="tit-product">옵션그룹</h4>
              <button
                type="button"
                className="btn-floating"
                disabled={isNoAuth1}
                onClick={() => {
                  setMenuScrollValue({
                    depth1: window.scrollY,
                    depth2: 0,
                  });
                  navigate(`/product/option?storeID=${storeId}`);
                }}
              >
                <span className="ico-plus" />
                옵션 그룹 추가
              </button>
              {optionGroup ? <ListElement list={optionGroup} /> : <MenuEmptyBox type="optionGroup" />}
            </div>
          ) : (
            // 옵션
            <div className="box-content">
              <h4 className="tit-product">
                <button
                  type="button"
                  className="link-back"
                  onClick={() => {
                    setType('optionGroup');
                    setOptGroupId('');
                  }}
                >
                  <span className="ico-arr" />
                </button>
                <span className="category-name">{selectedOptGroup?.title}</span>
                <div className="box-setting">
                  {selectedOptGroup?.isRequired ? (
                    <span className="txt-g">
                      주문 시 선택 가능 옵션 수량 <br />
                      <em className="js-option-group-type">주문 시 옵션을 필수적으로 선택해야 합니다.</em>
                    </span>
                  ) : (
                    <span className="txt-g">
                      주문 시 선택 가능 옵션 수량 <br />
                      <em className="js-option-group-type">주문 시 옵션을 추가적으로 선택할 수 있습니다.</em>
                    </span>
                  )}
                  {/* 선택 가능 옵션 수량 설정 화면 링크 */}
                  <CommonEditIconButton
                    disable={isNoAuth8}
                    onClick={() =>
                      navigate(
                        `/product/option?storeID=${storeId}&optionGroupID=${optGroupIdRef.current}&selectType=${selectedOptGroup?.isRequired}`,
                      )
                    }
                  />
                </div>
              </h4>
              <button
                type="button"
                className="btn-floating"
                disabled={isNoAuth9}
                onClick={() => {
                  setMenuScrollValue({
                    depth1: menuScrollValue.depth1,
                    depth2: window.scrollY,
                  });
                  navigate(`/product/option?storeID=${storeId}&optionGroupID=${optGroupIdRef.current}&options=1`);
                }}
              >
                <span className="ico-plus" />
                옵션 추가
              </button>
              {selectedOptGroup?.children?.length && <ListElement list={selectedOptGroup.children} />}
            </div>
          )}
          {/* 더보기 팝업 */}
          {clickedListRef.current && selectedOptGroup && (
            <BottomPopup
              isBottomPopup={isBottomPopup}
              onClickClose={() => setIsBottomPopup(false)}
              type={type}
              clickedList={clickedListRef.current}
              onClickDelete={() =>
                type === 'optionGroup' ? onClickOptGroupDelete() : onClickOptDelete(selectedOptGroup)
              }
              onClickCopy={() => onClickCopyOptGroup()}
            />
          )}
        </>
      )}
    </div>
  );
};

export default OptionManage;
